import React, { ReactElement } from 'react';

import s from './Portfolio.module.scss';
import { Project } from '../../shared/schema/ProjectSchema';
import { PropertyType } from '../../shared/schema/PropertySchema';

export const buyOptionsMap = {
  buy: 'Kaufen',
  rent: 'Mieten',
  buynrent: 'Kaufen & Mieten',
};

export const projectStatusMap = {
  planing: 'in Planung',
  building: 'in Bau',
  done: 'fertiggestellt',
};

export const projectTypeMap = {
  new: 'Neubau',
  old: 'Altbau',
  refurbishedOld: 'Sanierter Altbau',
  refurbishedOldWithRoofExpansion: 'Sanierter Altbau & Dachgeschossausbau',
  roofExpansion: 'Dachgeschossausbau',
};

export const financingMap = {
  free: 'frei finanziert',
  promoted: 'gefördert',
  freenpromoted: 'gefördert & frei finanziert',
};

const generateRange = (numbers) => {
  const max = Math.max(...numbers);
  const min = Math.min(...numbers);
  let range = '-';
  if (min > 0 && max >= min) {
    range = `${min} - ${max}`;
  }
  return range;
};

interface DataProps {
  project: Project;
}

export default function Data(props: DataProps): ReactElement {
  const {
    project: {
      rooms,
      area,
      flatCount,
      properties,
      financing,
      buyOptions,
      projectStatus,
      projectType,
      buildingDone,
    },
  } = props;

  let flatCountDisplay = '-';
  if (properties?.length > 0) {
    flatCountDisplay = properties.length;
  } else if (flatCount) {
    flatCountDisplay = flatCount;
  }
  let areaDisplay = '-';
  if (properties?.length > 0) {
    areaDisplay = `${generateRange(
      properties.map((p) => Math.round(p.area))
    )} m²`;
  } else if (area) {
    areaDisplay = area;
  }

  let roomsDisplay = '-';
  if (properties?.length > 0) {
    roomsDisplay = generateRange(properties.map((p) => p.rooms));
  } else if (rooms) {
    roomsDisplay = rooms;
  }

  const generateApartmentCountString = (apartmentCount) => {
    if (buyOptions === 'buy') {
      if (apartmentCount === 1) {
        return '1 Eigengtumswohnung';
      }
      return `${apartmentCount} Eigentumswohnungen`;
    }
    if (buyOptions === 'rent') {
      if (apartmentCount === 1) {
        return '1 Mietwohnungen';
      }
      return `${apartmentCount} Mietwohnungen`;
    }
    if (buyOptions === 'buynrent') {
      if (apartmentCount === 1) {
        return '1 Miet- und EIgentumswohnung';
      }
      return `${apartmentCount} Miet- und Eigentumswohnungen`;
    }
    return '-';
  };

  const generatePropertyCountString = () => {
    if (properties?.length > 0) {
      const propertyTypes = properties.map((property) => property.type);
      const houseCount = propertyTypes.filter(
        (type) => type === PropertyType.house
      ).length;
      const apartmentCount = propertyTypes.filter(
        (type) => type === PropertyType.apartment
      ).length;
      if (houseCount && apartmentCount) {
        let houseCountString = '';
        if (houseCount === 1) {
          houseCountString = '1 Haus';
        } else {
          houseCountString = `${houseCount} Häuser`;
        }

        return `${generateApartmentCountString(
          apartmentCount
        )} & ${houseCountString}`;
      }

      if (houseCount) {
        if (houseCount === 1) {
          return '1 Haus';
        }

        return `${houseCount} Häuser`;
      }

      if (apartmentCount) {
        return generateApartmentCountString(properties.length);
      }
    }
    return generateApartmentCountString(flatCountDisplay);
  };

  const data = [
    {
      key: 'Projektart',
      value: projectTypeMap[projectType] || '-',
    },
    {
      key: 'Finanzierungsform',
      value: financingMap[financing] || '-',
    },
    {
      key: 'Verfügbare Einheiten',
      value: generatePropertyCountString(),
    },
    {
      key: 'Zimmer',
      value: roomsDisplay,
    },
    {
      key: 'Wohnfläche',
      value: areaDisplay,
    },
    {
      key: 'Projektstatus',
      value: projectStatusMap[projectStatus] || '-',
    },
    {
      key: 'Fertigstellungsdatum',
      value: buildingDone || '-',
    },
  ];

  const renderRows = (rows) =>
    rows.map((r) => (
      <tr key={r.key}>
        <td>{r.key}</td>
        <td>{r.value}</td>
      </tr>
    ));

  return (
    <>
      <div className={s.subTitle}>Projektdaten</div>
      <div className={s.tableWrapper}>
        <table>
          <tbody>{renderRows(data)}</tbody>
        </table>
      </div>
    </>
  );
}
